import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ActionButton } from "@/components/elements/Button";
import ActionBar from "@/components/elements/layout/ActionBar";
import { useNavigate } from "@tanstack/react-location";

const ActionRefContext = createContext<{
  ref: HTMLDivElement | null;
  setRef: Dispatch<SetStateAction<HTMLDivElement | null>>;
  ownerId: string | null;
  setOwnerId: Dispatch<SetStateAction<string | null>>;
}>({
  ref: null,
  setRef: () => {},
  ownerId: null,
  setOwnerId: () => {},
});

function useActionRef() {
  const ctx = useContext(ActionRefContext);
  if (ctx == null) {
    throw new Error("ActionRefContext is not provided");
  }
  return ctx;
}

export const ActionContextProvider = (props: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);

  return (
    <ActionRefContext.Provider value={{ ref, setRef, ownerId, setOwnerId }}>
      {props.children}
    </ActionRefContext.Provider>
  );
};

export const TaskAction = ({ children }: { children: JSX.Element }) => {
  const { ref, ownerId, setOwnerId } = useActionRef();
  const id = useId(); // Unique ID per TaskAction

  useEffect(() => {
    // Claim ownership on mount
    setOwnerId(id);

    return () => {
      // Release ownership on unmount
      setOwnerId((current) => (current === id ? null : current));
    };
  }, [id, setOwnerId]);

  if (ref == null || ownerId !== id) {
    return null;
  }

  return createPortal(children, ref);
};

export const TaskActionBar = (props: {
  className?: string;
  noCancel?: boolean;
  onCancelClicked?: React.MouseEventHandler<HTMLButtonElement>;
}) => {
  const { setRef } = useActionRef();
  const navigate = useNavigate();

  return (
    <ActionBar className={props.className}>
      {!props.noCancel && (
        <ActionButton
          variant="ghost"
          colorScheme="secondary"
          onClick={(e) =>
            typeof props.onCancelClicked === "function"
              ? props.onCancelClicked(e)
              : navigate({ to: ".." })
          }
          style={{ minWidth: "0" }}
          tabIndex={-1}
        >
          Cancel
        </ActionButton>
      )}
      <div className="ml-auto shrink-0" ref={(ref) => ref && setRef(ref)} />
    </ActionBar>
  );
};
